import React from 'react';

import { faSpinnerThird, faPlus, faTrashAlt, faFileLines, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import {
    type CreatePricingPlanFormSchemaValues,
    useCreatePricingPlan,
    createPricingPlanFormSchema,
} from 'src/features/pricingPlans/api/useCreatePricingPlan';
import { useDeletePricingPlan } from 'src/features/pricingPlans/api/useDeletePricingPlan';
import { useUpdatePricingPlan } from 'src/features/pricingPlans/api/useUpdatePricingPlan';
import { PricingPlanForm } from 'src/features/pricingPlans/components/PricingPlanForm';
import { type PricingPlan, type PricingPlanDetails, TIME_TO_LEAVE_OPTIONS } from 'src/features/pricingPlans/types';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    isDialogOpen?: boolean;
    selectedPricingPlan?: PricingPlanDetails;
    children?: React.ReactNode;
    isDelete?: boolean;
    removeTrigger?: boolean;
    onCloseDialog?: () => void;
}

function PricingPlanFormModal<TData>({
    isDialogOpen = false,
    selectedPricingPlan,
    className,
    semantics,
    children,
    isDelete,
    removeTrigger,
    onSubmit: updateTable,
    onCloseDialog,
}: PricingPlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingRole } = useParking();
    const { parkingId } = useParams();

    const navigate = useNavigate();

    const { mutate: createPricingPlan, isPending: isPendingCreate } = useCreatePricingPlan({
        onSuccess: ({ plan_name, id }: PricingPlan) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(pricingPlansMessages.createPricingPlanSuccess, {
                    plan_name,
                }),
            });
            void navigate(id);
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updatePricingPlan, isPending: isPendingUpdate } = useUpdatePricingPlan({
        onSuccess: ({ plan_name }) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(pricingPlansMessages.updatePricingPlanSuccess, {
                    plan_name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: deletePricingPlan, isPending: isPendingDelete } = useDeletePricingPlan({
        onSuccess: ({ plan_name }) => {
            if (updateTable) updateTable();
            toast({
                title: formatMessage(pricingPlansMessages.deletePricingPlanSuccess, { plan_name }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const {
        plan_name = '',
        initial_price = 0,
        free_time = 0,
        vat = 0,
        time_to_leave = 0,
        time_to_leave_option = TIME_TO_LEAVE_OPTIONS.NORMAL,
        first_period = [
            {
                price_tick: 0,
                due_amount: 0,
                duration: 0,
            },
        ],
        second_period = [
            {
                price_tick: 0,
                due_amount: 0,
                duration: 0,
            },
        ],
        third_period = [
            {
                price_tick: 0,
                due_amount: 0,
            },
        ],
        special_period = [
            {
                in_use: false,
                period_start: 0,
                period_end: 0,
                price_tick: 0,
                due_amount: 0,
            },
        ],
    } = selectedPricingPlan || {};

    const form = useForm<CreatePricingPlanFormSchemaValues>({
        resolver: zodResolver(createPricingPlanFormSchema),
        defaultValues: {
            plan_name,
            initial_price,
            free_time,
            first_period,
            second_period,
            third_period,
            special_period,
            vat,
            time_to_leave,
            time_to_leave_option,
        },
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(selectedPricingPlan);
    }, [selectedPricingPlan]);

    const onSubmit = (values: CreatePricingPlanFormSchemaValues) => {
        if (!parkingId) return;
        const submitData = {
            parkingId: parkingId,
            ...values,
        };
        if (!selectedPricingPlan?.id) return createPricingPlan(submitData);
        if (isDelete && parkingId) return deletePricingPlan({ planId: selectedPricingPlan.id, parkingId: parkingId });
        return updatePricingPlan({ planId: selectedPricingPlan.id, ...submitData });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (setIsModalOpen) setIsModalOpen(isOpen);
        if (!isOpen) {
            form.reset();
            if (onCloseDialog) onCloseDialog();
        }
    };

    const getDialogTitle = () => {
        if (isDelete) {
            return formatMessage(pricingPlansMessages.deletePricingPlan);
        }
        if (selectedPricingPlan?.id) {
            return formatMessage(pricingPlansMessages.editPricingPlan);
        }
        return formatMessage(tableMessages.new, {
            entry: semantics?.entryName?.toLowerCase(),
        });
    };

    const getDialogDescription = () => {
        if (isDelete) {
            return formatMessage(pricingPlansMessages.deletePricingPlanDescription);
        }
        if (selectedPricingPlan?.id) {
            return formatMessage(pricingPlansMessages.editPricingPlanDescription);
        }
        return formatMessage(pricingPlansMessages.createNewPricingPlan);
    };

    const getDialogIcon = () => {
        if (isDelete) return faTrashAlt;
        if (selectedPricingPlan?.id) return faEdit;
        return faFileLines;
    };

    if (parkingRole == MEMBERSHIP_ROLES.CLIENT) return <></>;
    return (
        <Dialog modal open={isModalOpen || isDialogOpen} onOpenChange={onOpenChange}>
            {!children && !selectedPricingPlan && !removeTrigger && (
                <DialogTrigger asChild>
                    <Button variant="outline" className={cn('ml-auto', className)}>
                        <FontAwesomeIcon className="text-primary-700" icon={faPlus} />
                        {formatMessage(tableMessages.new, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogTrigger>
            )}
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader className={isDelete ? 'flex-row gap-4' : ''}>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={getDialogIcon()} />
                        <div>
                            <DialogTitle>{getDialogTitle()}</DialogTitle>
                            <DialogDescription>{getDialogDescription()}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />
                <DialogBody>
                    {isDelete ? (
                        <p className="text-gray-500">
                            {formatMessage(pricingPlansMessages.confirmDeletePricingPlan, {
                                plan: selectedPricingPlan?.plan_name,
                            })}
                        </p>
                    ) : (
                        <PricingPlanForm form={form} onSubmit={onSubmit} />
                    )}
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        variant={isDelete ? 'destructiveLight' : 'default'}
                        disabled={isPendingCreate || isPendingUpdate || isPendingDelete}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingCreate || isPendingUpdate || isPendingDelete ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(
                            isDelete
                                ? pricingPlansMessages.deletePricingPlan
                                : selectedPricingPlan?.id
                                  ? pricingPlansMessages.editPricingPlan
                                  : pricingPlansMessages.addPricingPlan,
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { PricingPlanFormModal };
